// https://github.com/charkour/zustand-di/
// Zustand are deprecating their context feature in favor of the react context
import React, {
  createContext as createReactContext,
  useContext,
  useRef,
  createElement,
} from "react"
import { StoreApi, useStore as useZustandStore } from "zustand"

type ExtractState<Store> = Store extends { getState: () => infer T } ? T : never

// Inspired from: https://github.com/pmndrs/zustand/blob/main/src/context.ts
export const createContext = <
  State,
  Store extends StoreApi<State> = StoreApi<State>,
>() => {
  const StoreContext = createReactContext<Store | undefined>(undefined)

  const Provider: React.FC<{
    createStore: () => Store
    children?: React.ReactNode
  }> = ({ createStore, children }) => {
    const storeRef = useRef<Store>(null)
    if (!storeRef.current) {
      storeRef.current = createStore()
    }
    return createElement(
      StoreContext.Provider,
      { value: storeRef.current },
      children,
    )
  }

  const useStore = <StateSlice = ExtractState<Store>>(
    selector: (state: ExtractState<Store>) => StateSlice = (state) =>
      state as StateSlice,
  ) => {
    const store = useContext(StoreContext)
    if (!store) {
      throw new Error(
        "Seems like you have not used zustand provider as an ancestor",
      )
    }
    return useZustandStore(store, selector)
  }

  return {
    Provider,
    useStore,
  }
}
